import { recordChanged } from 'helpers/record_changed';
import { isInlineToolCall } from './tools/inline';

import type { AskUserQuestionsToolCall } from './tools/inline/question_cards/types';
import type { AssistantAction, Message, StreamFrame } from './types';
import type { InlineToolCall, NextActionsProgress, RecommendationsToolCall } from './tools/types';

const GENERIC_ERROR = 'Something went wrong - please try again.';

const replaceLast = (messages: Message[], message: Message): Message[] => [...messages.slice(0, -1), message];

export function dropTrailingPlaceholder(messages: Message[]): Message[] {
  const last = messages[messages.length - 1];
  return last?.role === 'assistant' && !last.content && last.isStreaming ? messages.slice(0, -1) : messages;
}

// Ends the reply the user talked over: what streamed so far stands as the reply, an empty
// placeholder just goes
export function settleInterruptedTurn(messages: Message[]): Message[] {
  const last = messages[messages.length - 1];
  if (last?.role !== 'assistant' || !last.isStreaming) return messages;
  if (!last.content && !last.inlineToolCalls?.length) return messages.slice(0, -1);

  return replaceLast(messages, { ...last, isStreaming: false, status: undefined, steps: undefined });
}

export function markInterrupting(messages: Message[]): Message[] {
  const last = messages[messages.length - 1];
  return last?.role === 'user' ? replaceLast(messages, { ...last, interrupting: true }) : messages;
}

// `announced` is the caller's: an approved proposal arrives both on the apply response and on the
// resumed turn's done frame, and neither site can be dropped - an apply that does not resume the
// chat produces no done frame at all. Reads the frame's actions rather than the transcript because
// applyStreamFrame only keeps a pending one.
export function announceChanges(actions: AssistantAction[] | undefined, announced: Set<number>) {
  actions?.forEach((action) => {
    if (action.status !== 'applied' || announced.has(action.id)) return;

    announced.add(action.id);
    action.clientChanges?.forEach(recordChanged);
  });
}

export function updateActionStatuses(messages: Message[], updates: AssistantAction[]): Message[] {
  if (!updates.length) return messages;

  const byId = new Map(updates.map(update => [update.id, update]));
  return messages.map(message => (
    message.actions?.some(action => byId.has(action.id))
      ? { ...message, actions: message.actions.map(action => byId.get(action.id) ?? action) }
      : message
  ));
}

export function updateQuestionMetadata(
  messages: Message[],
  answerPath: string,
  patch: Pick<AskUserQuestionsToolCall['metadata'], 'answers' | 'dismissed'>
): Message[] {
  const matches = (toolCall: InlineToolCall): toolCall is InlineToolCall & AskUserQuestionsToolCall => (
    toolCall.name === 'ask_user_questions' && toolCall.metadata.answerPath === answerPath
  );

  return messages.map(message => (
    message.inlineToolCalls?.some(matches)
      ? {
        ...message,
        inlineToolCalls: message.inlineToolCalls.map(toolCall => (
          matches(toolCall) ? { ...toolCall, metadata: { ...toolCall.metadata, ...patch } } : toolCall
        )),
      }
      : message
  ));
}

export function nextActionsProgress(messages: Message[], toolCallId: number): NextActionsProgress {
  const call = messages
    .map(message => (message.inlineToolCalls || []).find(toolCall => toolCall.id === toolCallId))
    .find(Boolean);
  const metadata = call?.name === 'recommendations' ? call.metadata : {};

  return { cleared: metadata.cleared ?? [], dismissed: metadata.dismissed ?? false };
}

export function updateNextActionsMetadata(
  messages: Message[],
  toolCallId: number,
  progress: NextActionsProgress
): Message[] {
  const matches = (toolCall: InlineToolCall): toolCall is InlineToolCall & RecommendationsToolCall => (
    toolCall.name === 'recommendations' && toolCall.id === toolCallId
  );

  return messages.map(message => (
    message.inlineToolCalls?.some(matches)
      ? {
        ...message,
        inlineToolCalls: message.inlineToolCalls.map(toolCall => (
          matches(toolCall) ? { ...toolCall, metadata: { ...toolCall.metadata, ...progress } } : toolCall
        )),
      }
      : message
  ));
}

function withProgressFrom(previous: InlineToolCall[] = [], next: InlineToolCall[]): InlineToolCall[] {
  return next.map((toolCall) => {
    const before = previous.find(({ id }) => id === toolCall.id);
    if (toolCall.name !== 'recommendations' || before?.name !== 'recommendations') {
      return toolCall;
    }

    const { cleared, dismissed } = before.metadata;
    const progress = { ...(cleared && { cleared }), ...(dismissed && { dismissed }) };
    return { ...toolCall, metadata: { ...toolCall.metadata, ...progress } };
  });
}

// Applies a stream frame to the message list, mutating the trailing assistant placeholder
// that `sendMessage` appended on submit. Pure so it can be unit-tested and used inside a
// functional `setMessages` update.
//
// `done.content` is authoritative: halting tools (Navigate/Support) end the turn without
// streaming any deltas, so their reply exists only on the terminal frame. Inline tool calls
// are transcript content and land on the message; the rest are side effects the caller runs.
export function applyStreamFrame(messages: Message[], frame: StreamFrame): Message[] {
  const last = messages[messages.length - 1];
  if (!last) return messages;

  switch (frame.type) {
  case 'status':
    return replaceLast(messages, { ...last, status: frame.label });
  case 'steps':
    return replaceLast(messages, { ...last, steps: frame.steps });
  case 'step_done':
    return replaceLast(messages, {
      ...last,
      steps: last.steps?.map(step => (step.key === frame.key ? { ...step, done: true } : step)),
    });
  case 'delta':
    return replaceLast(messages, { ...last, content: last.content + frame.text, status: undefined });
  case 'tool_calls':
    return replaceLast(messages, {
      ...last,
      inlineToolCalls: withProgressFrom(last.inlineToolCalls, frame.toolCalls.filter(isInlineToolCall)),
    });
  case 'done': {
    const updated = updateActionStatuses(messages, frame.actions ?? []);
    const known = new Set<number>();
    messages.forEach(message => message.actions?.forEach(action => known.add(action.id)));
    const fresh = (frame.actions ?? []).filter(action => action.status === 'pending' && !known.has(action.id));
    const trailing = updated[updated.length - 1];
    const toolCalls = frame.toolCalls?.filter(isInlineToolCall);

    return replaceLast(updated, {
      ...trailing,
      content: frame.content || trailing.content,
      inlineToolCalls: toolCalls && withProgressFrom(trailing.inlineToolCalls, toolCalls),
      metrics: frame.metrics,
      actions: fresh.length ? [...(trailing.actions ?? []), ...fresh] : trailing.actions,
      isStreaming: false,
      status: undefined,
    });
  }
  case 'error':
    return replaceLast(messages, {
      ...last,
      content: last.content || GENERIC_ERROR,
      isStreaming: false,
      status: undefined,
      error: true,
    });
  default:
    return messages;
  }
}
